import {
  ActionOutboxEntityType,
  CatalogResults,
  Event,
  EventEntities,
  EventEntityCounts,
  EventWithData,
  Listing,
  ListingWithCatalogData,
  Performer,
  Sale,
  SaleWithCatalogData,
  TopLevelCategory,
  Venue,
} from 'src/WebApiController';

import { SearchDataWithEvent } from './SearchDataContext.types';

type EntityWithCatalogData = ListingWithCatalogData | SaleWithCatalogData;

interface ExtractedEntities {
  items: EntityWithCatalogData[] | null;
  summary: EntityWithCatalogData | null;
  sample: EntityWithCatalogData | null;
}

/**
 * Extracts items, summary, and a sample entity from event data based on entity type.
 */
function extractEntitiesFromEventData(
  eventData: SearchDataWithEvent[string],
  entityType: ActionOutboxEntityType
): ExtractedEntities {
  if (entityType === ActionOutboxEntityType.Sale) {
    const items = eventData.sales;
    const summary = eventData.salesSummary;
    return {
      items,
      summary,
      sample: items?.[0] ?? summary,
    };
  }
  const items = eventData.listings;
  const summary = eventData.listingsSummary;
  return {
    items,
    summary,
    sample: items?.[0] ?? summary,
  };
}

/**
 * Builds an Event object from embedded catalog data or falls back to existing event data.
 */
function buildEventFromCatalogData(
  sample: EntityWithCatalogData,
  viagogoVirtualId: string,
  eventFallback?: Event
): Event {
  if (sample.eventName) {
    return {
      viagId: sample.viagEvId,
      name: sample.eventName ?? '',
      shortName: sample.eventShortName ?? null,
      rowVer: null,
      posIds: sample.posEvId ? [sample.posEvId] : [],
      posId: sample.posEvId,
      virtId: viagogoVirtualId,
      oldVirtIds: null,
      mpId: sample.viagEvMapId,
      dates: {
        start: sample.eventDateTime ?? null,
        end: null,
      },
      datesUtc: {
        start: null,
        end: null,
      },
      onSaleDate: null,
      rawOnSaleDate: null,
      rawPreSaleDate: null,
      evType: null,
      evStat: sample.eventStatus ?? null,
      perfId: sample.performerId ?? null,
      venId: sample.venueId ?? 0,
      venCfgId: null,
      genre: sample.eventGenre ?? TopLevelCategory.Concert,
      mapRejRsnId: null,
      mapRejDftTxt: null,
    };
  }
  return eventFallback!;
}

/**
 * Builds and adds a Performer to catalog results from embedded data or fallback.
 */
function buildPerformerData(
  sample: EntityWithCatalogData,
  catalogResults: CatalogResults,
  catalogDataFallback?: CatalogResults,
  eventFallback?: Event
): void {
  if (sample.performerId != null) {
    const performer: Performer = {
      viagId: sample.performerId,
      name: sample.performerName ?? '',
      shortName: null,
      categ: [],
      genreId: null,
      grName: null,
    };
    catalogResults.performers[sample.performerId] = performer;
  } else if (eventFallback?.perfId != null) {
    const performerFallback =
      catalogDataFallback?.performers[eventFallback.perfId];
    if (performerFallback) {
      catalogResults.performers[eventFallback.perfId] = performerFallback;
    }
  }
}

/**
 * Builds and adds a Venue to catalog results from embedded data or fallback.
 */
function buildVenueData(
  sample: EntityWithCatalogData,
  catalogResults: CatalogResults,
  catalogDataFallback?: CatalogResults,
  eventFallback?: Event
): void {
  if (sample.venueId != null) {
    const venue: Venue = {
      viagId: sample.venueId,
      name: sample.venueName ?? '',
      shortName: null,
      city: sample.venueCity ?? null,
      state: sample.venueStateProvince ?? null,
      ctryCode: sample.venueCountryCode ?? null,
      cap: null,
      excludeSearch: false,
    };
    catalogResults.venues[sample.venueId] = venue;
  } else if (eventFallback?.venId != null) {
    const venueFallback = catalogDataFallback?.venues[eventFallback.venId];
    if (venueFallback) {
      catalogResults.venues[eventFallback.venId] = venueFallback;
    }
  }
}

/**
 * Builds EventEntityCounts for either sales or listings.
 */
function buildEntityCounts(
  viagogoVirtualId: string,
  sample: EntityWithCatalogData,
  itemCount: number,
  entityType: ActionOutboxEntityType
): EventEntityCounts {
  return {
    viagVirtId: viagogoVirtualId,
    perfId: sample.performerId ?? null,
    venId: sample.venueId ?? 0,
    rowVer: null,
    salesCnt: entityType === ActionOutboxEntityType.Sale ? itemCount : 0,
    listCnt: entityType === ActionOutboxEntityType.Listing ? itemCount : 0,
    pricedListCnt: 0,
    ungrListCnt: 0,
    listGrpCnt: 0,
    tktGrpsCnt: 0,
    poCnt: 0,
  };
}

/**
 * Builds EventEntities for either sales or listings.
 */
function buildEventEntities(
  eventData: SearchDataWithEvent[string],
  entityType: ActionOutboxEntityType,
  items: EntityWithCatalogData[] | null,
  summary: EntityWithCatalogData | null
): EventEntities {
  return {
    listings:
      entityType === ActionOutboxEntityType.Listing
        ? (items as Listing[])
        : null,
    listingsSummary:
      entityType === ActionOutboxEntityType.Listing
        ? (summary as Listing)
        : null,
    sales:
      entityType === ActionOutboxEntityType.Sale ? (items as Sale[]) : null,
    salesSummary:
      entityType === ActionOutboxEntityType.Sale ? (summary as Sale) : null,
    ticketGroups: null,
    ticketGroupsSummary: null,
    getDataFail: eventData.failedToRetrieveData ?? null,
    pitId: eventData.pitId ?? null,
    totalItemsCount: eventData.totalItemsCount ?? null,
  };
}

/**
 * Creates an empty CatalogResults object.
 */
function createEmptyCatalogResults(): CatalogResults {
  return {
    events: {},
    performers: {},
    venues: {},
    viagIdToLatestIdLookup: {},
    posIdToViagIdLookup: {},
    countries: {},
    purchaseCount: 0,
  };
}

/**
 * Generic function to build CatalogResults and EventWithData[] from SearchDataWithEvent.
 * Works for both sales and listings by extracting embedded catalog data.
 */
function buildCatalogAndEventsFromSearchData(
  searchData: SearchDataWithEvent,
  entityType: ActionOutboxEntityType,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  const catalogResults = createEmptyCatalogResults();
  const eventsTransformed: EventWithData[] = [];

  for (const [viagogoVirtualId, eventData] of Object.entries(searchData)) {
    const { items, summary, sample } = extractEntitiesFromEventData(
      eventData,
      entityType
    );

    if (!sample) {
      continue;
    }

    const eventFallback = catalogDataFallback?.events[viagogoVirtualId]?.event;

    if (!sample.eventName && !eventFallback) {
      continue;
    }

    const event = buildEventFromCatalogData(
      sample,
      viagogoVirtualId,
      eventFallback
    );

    buildPerformerData(
      sample,
      catalogResults,
      catalogDataFallback,
      eventFallback
    );
    buildVenueData(sample, catalogResults, catalogDataFallback, eventFallback);

    const itemCount = eventData.totalItemsCount ?? items?.length ?? 0;
    const counts = buildEntityCounts(
      viagogoVirtualId,
      sample,
      itemCount,
      entityType
    );

    const entities = buildEventEntities(eventData, entityType, items, summary);

    const eventWithData: EventWithData = {
      event,
      counts,
      entities,
    };

    catalogResults.events[viagogoVirtualId] = eventWithData;
    catalogResults.viagIdToLatestIdLookup[viagogoVirtualId] = viagogoVirtualId;
    eventsTransformed.push(eventWithData);
  }

  return { catalogResults, eventsTransformed };
}

/**
 * Builds CatalogResults and EventWithData[] from SearchDataWithEvent for sales.
 * This extracts embedded catalog data from SaleWithCatalogData objects.
 */
export function buildCatalogAndEventsFromSalesSearchData(
  searchData: SearchDataWithEvent,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  return buildCatalogAndEventsFromSearchData(
    searchData,
    ActionOutboxEntityType.Sale,
    catalogDataFallback
  );
}

/**
 * Builds CatalogResults and EventWithData[] from SearchDataWithEvent for listings.
 * This extracts embedded catalog data from ListingWithCatalogData objects.
 */
export function buildCatalogAndEventsFromListingsSearchData(
  searchData: SearchDataWithEvent,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  return buildCatalogAndEventsFromSearchData(
    searchData,
    ActionOutboxEntityType.Listing,
    catalogDataFallback
  );
}
